<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import OnboardingAPI from 'dashboard/api/onboarding';

const props = defineProps({
  step: { type: Number, required: true },
  progress: { type: Object, required: true },
  // Set by the step's own content when it already holds a submitted draft that
  // isn't reflected in completed_steps/deferred_steps yet — e.g. the user filled
  // the form and clicked the wizard's own "Voltar" instead of "Próximo", so
  // markCurrentStepCompletedIfNeeded() never ran. Without this, the question
  // would incorrectly reopen and hide the already-filled content.
  hasDraft: { type: Boolean, default: false },
  // Optional per-consumer overrides — falls back to the generic ONBOARDING.OPTIONAL_GATE
  // copy when not provided, since most steps share the same gate wording.
  question: { type: String, default: '' },
  nowLabel: { type: String, default: '' },
  laterLabel: { type: String, default: '' },
  // Secondary line shown under each card's label — omitted entirely when not provided.
  nowDescription: { type: String, default: '' },
  laterDescription: { type: String, default: '' },
});
const emit = defineEmits(['update:can-advance', 'refresh', 'chooseYes']);

const { t } = useI18n();

const isCompleted = computed(() =>
  props.progress.completed_steps.includes(props.step)
);
const isDeferred = computed(() =>
  props.progress.deferred_steps.includes(props.step)
);

// Skips the question entirely once completed (ADR-018); reopens on "no" from a
// previous session, but the user may still switch to "yes" at any time.
const initialChoice = () => {
  if (isCompleted.value || props.hasDraft) return 'yes';
  if (isDeferred.value) return 'no';
  return null;
};
const choice = ref(initialChoice());

// The question stays above the form for as long as there's a real choice to
// make — it doesn't disappear once answered, since the user can still switch
// their mind between "now" and "later" (see chooseYes/chooseNo below).
const showQuestion = computed(() => !isCompleted.value && !props.hasDraft);
// The form itself is always rendered (never hidden) so the user can see what
// they're being asked to fill in; it's only interactive once they've opted in.
const isFormEnabled = computed(
  () => isCompleted.value || props.hasDraft || choice.value === 'yes'
);

// A resolved gate (already completed, deferred, or holding a pre-existing draft) must
// never leave Next disabled. The step's own watch(isValid, { immediate: true }) fires
// synchronously during its setup — before this component even mounts — and emits false
// whenever there's nothing valid yet (e.g. a freshly reopened "later" step has no draft).
// Re-clicking the already-selected "Depois" radio doesn't fire a native change event
// either, so without this the user would be stuck: unable to advance or re-defer. This
// runs once, after that emit, and only overrides it for states already resolved by the
// gate itself — it doesn't fight the step's own validation once the user starts editing.
onMounted(() => {
  if (isCompleted.value || isDeferred.value || props.hasDraft) {
    emit('update:can-advance', true);
  }
});

const chooseYes = async () => {
  choice.value = 'yes';
  emit('chooseYes');
  // A step reopened from deferred_steps must be cleared so the wizard's own
  // completion tracking (markCurrentStepCompletedIfNeeded) can mark it as
  // completed_steps once its form is actually filled in and Next is pressed.
  if (!isDeferred.value) return;

  try {
    await OnboardingAPI.update({
      deferred_steps: props.progress.deferred_steps.filter(
        step => step !== props.step
      ),
    });
    emit('refresh');
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
};

const chooseNo = async () => {
  choice.value = 'no';
  try {
    await OnboardingAPI.update({
      deferred_steps: [
        ...new Set([...props.progress.deferred_steps, props.step]),
      ],
    });
    emit('refresh');
    emit('update:can-advance', true);
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
};
</script>

<template>
  <div class="flex flex-col items-start justify-start w-full h-full gap-4">
    <div v-if="showQuestion" class="flex flex-col items-start w-full gap-3">
      <p class="text-n-slate-11">
        {{ question || t('ONBOARDING.OPTIONAL_GATE.QUESTION') }}
      </p>
      <div
        role="radiogroup"
        class="grid w-full grid-cols-1 gap-3 sm:grid-cols-2"
      >
        <label
          class="flex flex-col items-start gap-1 p-4 text-left border rounded-lg cursor-pointer border-n-weak"
          :class="choice === 'yes' ? 'border-n-brand bg-n-brand/5' : ''"
        >
          <input
            type="radio"
            name="optional-step-gate-choice"
            class="sr-only"
            :checked="choice === 'yes'"
            @change="chooseYes"
          />
          <span class="font-medium text-n-slate-12">
            {{ nowLabel || t('ONBOARDING.OPTIONAL_GATE.NOW') }}
          </span>
          <span v-if="nowDescription" class="text-sm text-n-slate-11">
            {{ nowDescription }}
          </span>
        </label>

        <label
          class="flex flex-col items-start gap-1 p-4 text-left border rounded-lg cursor-pointer border-n-weak"
          :class="choice === 'no' ? 'border-n-brand bg-n-brand/5' : ''"
        >
          <input
            type="radio"
            name="optional-step-gate-choice"
            class="sr-only"
            :checked="choice === 'no'"
            @change="chooseNo"
          />
          <span class="font-medium text-n-slate-12">
            {{ laterLabel || t('ONBOARDING.OPTIONAL_GATE.LATER') }}
          </span>
          <span v-if="laterDescription" class="text-sm text-n-slate-11">
            {{ laterDescription }}
          </span>
        </label>
      </div>
    </div>

    <div
      class="flex flex-col items-start justify-start w-full flex-1 transition-opacity"
      :class="{ 'opacity-50': !isFormEnabled }"
      :inert="isFormEnabled ? undefined : true"
    >
      <slot />
    </div>
  </div>
</template>
